import React, { useState, useEffect } from 'react';
import './NavBar.css';

const NavBar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Scroll spy - track which section is currently in view
  useEffect(() => {
    const sections = document.querySelectorAll('.page-section[id]');

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the entry with the highest intersection ratio
        let maxRatio = 0;
        let bestEntry: IntersectionObserverEntry | undefined;

        entries.forEach((entry) => {
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            bestEntry = entry;
          }
        });

        // Update active section if we found one that's intersecting
        if (bestEntry && bestEntry.isIntersecting) {
          setActiveSection(bestEntry.target.id);
        }
      },
      {
        rootMargin: '-100px 0px -66% 0px', // Trigger when section is in top third of viewport
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  // Smooth scroll handler
  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMenuOpen(false); // Close mobile menu
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <a href="#home" className="navbar-brand" onClick={(e) => handleSmoothScroll(e, 'home')}>
          <div className="brand-content">
            <img
              src="/logo/ThirdLocustWordmark.png"
              alt="Third Locust Capital Management"
              className="navbar-logo"
            />
          </div>
        </a>

        <button
          className={`navbar-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle navigation"
        >
          <i className={isMenuOpen ? "bi bi-x-lg" : "bi bi-list"}></i>
        </button>

        <div className={`navbar-menu ${isMenuOpen ? 'active' : ''}`}>
          <a
            href="#home"
            className={`navbar-link ${activeSection === 'home' ? 'active' : ''}`}
            onClick={(e) => handleSmoothScroll(e, 'home')}
          >
            Home
          </a>
          <a
            href="#story"
            className={`navbar-link ${activeSection === 'story' ? 'active' : ''}`}
            onClick={(e) => handleSmoothScroll(e, 'story')}
          >
            Story
          </a>
          <a
            href="#team"
            className={`navbar-link ${activeSection === 'team' ? 'active' : ''}`}
            onClick={(e) => handleSmoothScroll(e, 'team')}
          >
            Team
          </a>
          <a
            href="#contact"
            className={`navbar-link ${activeSection === 'contact' ? 'active' : ''}`}
            onClick={(e) => handleSmoothScroll(e, 'contact')}
          >
            Contact
          </a>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
